import React from 'react'
import { Typography } from '@mui/material'
import { Confirm, Form, useRefresh, useTranslate } from 'react-admin'
import { concat, isEmpty, isNil, map, replace, size, split } from 'lodash/fp'
import log from 'loglevel'

import {
  doCognitoUpload,
  doEditDataUpload,
  doMappingsUpload,
  doSolarAccountsDelete,
  doSolarAccountsUpload,
  doUpload
} from './functions'
import { type Partner } from '@mzc-watchover/adminapp-appsync'
import { useNotify } from 'components/Util'

log.setLevel((process.env['LOG_LEVEL'] as log.LogLevelDesc) ?? log.levels.DEBUG)

export const UploadDialog: React.FC<{
  open: boolean
  setOpen: (open: boolean) => void
  partner: Partial<Partner> | undefined
}> = ({ open, setOpen, partner }) => {
  const translate = useTranslate()
  const notify = useNotify()
  const inputRef = React.useRef<HTMLInputElement>(null)
  const upload = (event: React.ChangeEvent<HTMLInputElement>): void => {
    if (isNil(event.target.files) || isNil(event.target.files[0])) return
    notify('resources.upload.notification.uploading', {
      autoHideDuration: null
    })
    const reader = new FileReader()
    reader.onload = () => {
      if (isNil(reader.result)) return
      const file = replace(
        /^data:application\/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64,/,
        ''
      )(reader.result as string)
      doUpload(file, partner)
        .then((_status) => {})
        .catch(log.error)
        .finally(() => {
          notify('resources.upload.notification.uploading', {
            autoHideDuration: 0
          })
          setOpen(false)
        })
    }
    reader.readAsDataURL(event.target.files[0])
    event.target.value = ''
  }
  const onClose = (): void => {
    setOpen(false)
  }
  const onConfirm = (): void => {
    setOpen(false)
    if (!isNil(inputRef.current)) {
      inputRef.current.click()
    }
  }
  return (
    <Form>
      <Confirm
        isOpen={open}
        title={false}
        content={
          // @ts-expect-error ts(2339)
          map.convert({ cap: false })((item: string, index: number) => (
            <Typography variant="body1" key={index}>
              {item}
              <br />
            </Typography>
          ))(split('\n')(translate('resources.upload.dialog.content')))
        }
        onClose={onClose}
        onConfirm={onConfirm}
        cancel="resources.upload.dialog.buttons.cancel"
        confirm="resources.upload.dialog.buttons.confirm"
      />
      <input
        hidden
        ref={inputRef}
        type="file"
        accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        onChange={upload}
      />
    </Form>
  )
}
export const CognitoUploadDialog: React.FC<{
  open: boolean
  setOpen: (open: boolean) => void
}> = ({ open, setOpen }) => {
  const translate = useTranslate()
  const notify = useNotify()
  const inputRef = React.useRef<HTMLInputElement>(null)
  const upload = (event: React.ChangeEvent<HTMLInputElement>): void => {
    if (isNil(event.target.files) || isNil(event.target.files[0])) return
    notify('resources.cognito.upload.notification.uploading')
    const reader = new FileReader()
    reader.onload = () => {
      if (isNil(reader.result)) return
      const file = replace(
        /^data:application\/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64,/,
        ''
      )(reader.result as string)
      doCognitoUpload(file)
        .then((_status) => {})
        .catch(log.error)
        .finally(() => {
          setOpen(false)
        })
    }
    reader.readAsDataURL(event.target.files[0])
    event.target.value = ''
  }
  const onClose = (): void => {
    setOpen(false)
  }
  const onConfirm = (): void => {
    if (!isNil(inputRef.current)) {
      inputRef.current.click()
    }
  }
  return (
    <Form>
      <Confirm
        isOpen={open}
        title={false}
        content={
          // @ts-expect-error ts(2339)
          map.convert({ cap: false })((item: string, index: number) => (
            <Typography variant="body1" key={index}>
              {item}
              <br />
            </Typography>
          ))(split('\n')(translate('resources.cognito.upload.dialog.content')))
        }
        onClose={onClose}
        onConfirm={onConfirm}
        cancel="resources.cognito.upload.dialog.buttons.cancel"
        confirm="resources.cognito.upload.dialog.buttons.confirm"
      />
      <input
        hidden
        ref={inputRef}
        type="file"
        accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        onChange={upload}
      />
    </Form>
  )
}
export const SolarAccountsUploadDialog: React.FC<{
  open: boolean
  setOpen: (open: boolean) => void
  partner: Partial<Partner>
}> = ({ open, setOpen, partner }) => {
  const refresh = useRefresh()
  const translate = useTranslate()
  const notify = useNotify()
  const [loading, setLoading] = React.useState(false)
  const [deleteOpen, setDeleteOpen] = React.useState(false)
  const [deleteAccounts, setDeleteAccounts] = React.useState<any | undefined>(
    undefined
  )
  const inputRef = React.useRef<HTMLInputElement>(null)
  const upload = (event: React.ChangeEvent<HTMLInputElement>): void => {
    if (isNil(event.target.files) || isNil(event.target.files[0])) return
    setLoading(true)
    notify('resources.cognito.upload.notification.uploading', {
      autoHideDuration: null
    })
    const reader = new FileReader()
    reader.onload = () => {
      if (isNil(reader.result)) return
      const file = replace(
        /^data:application\/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64,/,
        ''
      )(reader.result as string)
      doSolarAccountsUpload(file, partner)
        .then(({ result }) => {
          if (!isNil(result)) {
            const { addAccounts, updateAccounts, deleteAccounts } = result
            notify('resources.cognito.upload.notification.result', {
              messageArgs: {
                addAccounts: size(addAccounts),
                updateAccounts: size(updateAccounts)
                // deleteAccounts: size(deleteAccounts)
              }
            })
            refresh()
            if (!isEmpty(deleteAccounts)) {
              setDeleteAccounts(deleteAccounts)
              setDeleteOpen(true)
            }
          }
        })
        .catch(() => {
          notify('resources.cognito.upload.notification.uploadError')
        })
        .finally(() => {
          notify('resources.cognito.upload.notification.uploading', {
            autoHideDuration: 0
          })
          setLoading(false)
          setOpen(false)
        })
    }
    reader.readAsDataURL(event.target.files[0])
    event.target.value = ''
  }
  const onClose = (): void => {
    setOpen(false)
  }
  const onConfirm = (): void => {
    if (!isNil(inputRef.current)) {
      inputRef.current.click()
    }
  }
  const onCloseDelete = (): void => {
    setDeleteOpen(false)
  }
  const doDelete = (): void => {
    notify('resources.cognito.upload.notification.deleting', {
      autoHideDuration: null
    })
    doSolarAccountsDelete(deleteAccounts, partner)
      .then(({ result }) => {
        if (!isNil(result)) {
          notify('resources.cognito.upload.notification.deleteResult')
          refresh()
        } else {
          notify('resources.cognito.upload.notification.deleteError')
        }
      })
      .catch(() => {
        notify('resources.cognito.upload.notification.deleteError')
      })
      .finally(() => {
        notify('resources.cognito.upload.notification.deleting', {
          autoHideDuration: 0
        })
        setDeleteOpen(false)
      })
  }
  // const onFormat = (): void => {
  //   doSolarAccountsDownloadFormat(partner)
  //     .then(({ data }) => {
  //       if (!isNil(data)) {
  //         const binaryString = window.atob(data)
  //         const len = binaryString.length
  //         const bytes = new Uint8Array(len)
  //         for (let i = 0; i < len; ++i) {
  //           bytes[i] = binaryString.charCodeAt(i)
  //         }
  //         const blob = new Blob([bytes], {
  //           type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  //         })
  //         const url = URL.createObjectURL(blob)
  //         const download = document.createElement('a')
  //         download.href = url
  //         download.download = 'format.xlsx'
  //         download.click()
  //         window.URL.revokeObjectURL(url)
  //       }
  //     })
  //     .catch(log.error)
  //     .finally(() => {
  //       setOpen(false)
  //     })
  // }
  return (
    <>
      <Form>
        <Confirm
          isOpen={open}
          title={false}
          content={concat(
            // @ts-expect-error ts(2339)
            map.convert({ cap: false })((item: string, index: number) => (
              <Typography variant="body1" key={index}>
                {item}
                <br />
              </Typography>
            ))(
              split('\n')(translate('resources.cognito.upload.dialog.content'))
            )
          )([
            // <Button sx={{ mt: 1 }} onClick={onFormat} key="format">
            //   <Typography variant="body1" key="url">
            //     {translate('resources.cognito.upload.dialog.buttons.format')}
            //   </Typography>
            // </Button>
          ])}
          loading={loading}
          onClose={onClose}
          onConfirm={onConfirm}
          cancel="resources.cognito.upload.dialog.buttons.cancel"
          confirm="resources.cognito.upload.dialog.buttons.confirm"
        />
        <input
          hidden
          ref={inputRef}
          type="file"
          accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          onChange={upload}
        />
      </Form>
      <Confirm
        isOpen={deleteOpen}
        title={false}
        content={concat(
          // @ts-expect-error ts(2339)
          map.convert({ cap: false })((item: string, index: number) => (
            <Typography variant="body1" key={index}>
              {item}
              <br />
            </Typography>
          ))(
            split('\n')(
              translate('resources.cognito.upload.delete.dialog.content', {
                deleteAccounts: size(deleteAccounts)
              })
            )
          )
        )([
          // <Button sx={{ mt: 1 }} onClick={onFormat} key="format">
          //   <Typography variant="body1" key="url">
          //     {translate('resources.cognito.upload.dialog.buttons.format')}
          //   </Typography>
          // </Button>
        ])}
        loading={loading}
        onClose={onCloseDelete}
        onConfirm={doDelete}
        cancel="resources.cognito.upload.delete.dialog.buttons.cancel"
        confirm="resources.cognito.upload.delete.dialog.buttons.confirm"
      />
    </>
  )
}
export const MappingsUploadDialog: React.FC<{
  open: boolean
  setOpen: (open: boolean) => void
  partner: Partial<Partner>
}> = ({ open, setOpen, partner }) => {
  const refresh = useRefresh()
  const translate = useTranslate()
  const notify = useNotify()
  const [loading, setLoading] = React.useState(false)
  const inputRef = React.useRef<HTMLInputElement>(null)
  const upload = (event: React.ChangeEvent<HTMLInputElement>): void => {
    if (isNil(event.target.files) || isNil(event.target.files[0])) return
    setLoading(true)
    notify('resources.mappings.upload.notification.uploading', {
      autoHideDuration: null
    })
    const reader = new FileReader()
    reader.onload = () => {
      if (isNil(reader.result)) return
      const file = replace(
        /^data:application\/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64,/,
        ''
      )(reader.result as string)
      doMappingsUpload(file, partner)
        .then(({ result }) => {
          if (!isNil(result)) {
            notify('resources.mappings.upload.notification.result', {
              messageArgs: {
                updates: size(result)
              }
            })
            if (!isEmpty(result)) refresh()
          }
        })
        .catch(() => {
          notify('resources.mappings.upload.notification.uploadError')
        })
        .finally(() => {
          notify('resources.mappings.upload.notification.uploading', {
            autoHideDuration: 0
          })
          setLoading(false)
          setOpen(false)
        })
    }
    reader.readAsDataURL(event.target.files[0])
    event.target.value = ''
  }
  const onClose = (): void => {
    setOpen(false)
  }
  const onConfirm = (): void => {
    if (!isNil(inputRef.current)) {
      inputRef.current.click()
    }
  }
  return (
    <>
      <Confirm
        isOpen={open}
        title={false}
        content={
          // @ts-expect-error ts(2339)
          map.convert({ cap: false })((item: string, index: number) => (
            <Typography variant="body1" key={index}>
              {item}
              <br />
            </Typography>
          ))(split('\n')(translate('resources.mappings.upload.dialog.content')))
        }
        loading={loading}
        onClose={onClose}
        onConfirm={onConfirm}
        cancel="resources.cognito.upload.dialog.buttons.cancel"
        confirm="resources.cognito.upload.dialog.buttons.confirm"
      />
      <input
        hidden
        ref={inputRef}
        type="file"
        accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        onChange={upload}
      />
    </>
  )
}
export const EditDataUploadDialog: React.FC<{
  open: boolean
  setOpen: (open: boolean) => void
  partner: Partial<Partner>
}> = ({ open, setOpen, partner }) => {
  const translate = useTranslate()
  const notify = useNotify()
  const [loading, setLoading] = React.useState(false)
  const inputRef = React.useRef<HTMLInputElement>(null)
  const upload = (event: React.ChangeEvent<HTMLInputElement>): void => {
    if (isNil(event.target.files) || isNil(event.target.files[0])) return
    setLoading(true)
    notify('resources.editData.upload.notification.uploading', {
      autoHideDuration: null
    })
    const reader = new FileReader()
    reader.onload = () => {
      if (isNil(reader.result)) return
      const file = replace(
        /^data:application\/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64,/,
        ''
      )(reader.result as string)
      doEditDataUpload(file, partner)
        .then(() => {
          notify('resources.editData.upload.notification.done')
        })
        .catch(() => {
          notify('resources.editData.upload.notification.uploadError')
        })
        .finally(() => {
          notify('resources.editData.upload.notification.uploading', {
            autoHideDuration: 0
          })
          setLoading(false)
          setOpen(false)
        })
    }
    reader.readAsDataURL(event.target.files[0])
    event.target.value = ''
  }
  const onClose = (): void => {
    setOpen(false)
  }
  const onConfirm = (): void => {
    if (!isNil(inputRef.current)) {
      inputRef.current.click()
    }
  }
  return (
    <>
      <Confirm
        isOpen={open}
        title={false}
        content={
          // @ts-expect-error ts(2339)
          map.convert({ cap: false })((item: string, index: number) => (
            <Typography variant="body1" key={index}>
              {item}
              <br />
            </Typography>
          ))(split('\n')(translate('resources.editData.upload.dialog.content')))
        }
        loading={loading}
        onClose={onClose}
        onConfirm={onConfirm}
        cancel="resources.editData.upload.dialog.buttons.cancel"
        confirm="resources.editData.upload.dialog.buttons.confirm"
      />
      <input
        hidden
        ref={inputRef}
        type="file"
        accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        onChange={upload}
      />
    </>
  )
}
